import { useContext, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import ContactSection from "../components/contactSection.jsx";
import ContentText from "../components/contentText.jsx";
import {
  breadcrumbJsonLd,
  CategoryTiles,
  itemListJsonLd,
  MachineGrid,
} from "../components/machineCollection.jsx";
import PageHero from "../components/pageHero.jsx";
import { MachineContext } from "../context/MachineContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { absoluteUrl, metaDescription } from "../seoUtils";
import { categoryUrlSlug } from "../slug.js";

/**
 * /{locale}/catalogue/:slug — one page per category.
 *
 * The categories are what buyers actually search for ("begagnad kantlistmaskin",
 * "plattuppdelningssåg"), and a ?category= filter on the catalogue is not a page: it
 * canonicalizes away and has nothing to rank with. This is that page, rendered on the server
 * from the same data the catalogue uses, with the admin-managed category description as its copy.
 */

/**
 * "CNC Nesting – CNC-maskiner till snickeriet | AS Maskiner" for the CNC categories,
 * "Spånsug till snickeriet | AS Maskiner" otherwise.
 *
 * No condition in the title. "Nya och begagnade" described the business, not the stock, and
 * with a catalogue that is entirely new it pulled in searchers for used machines who found
 * none. The stock line on the page reports the real mix (StockSummary) and will say
 * "begagnade" the day a used machine is listed.
 */
export function categoryTitle(name) {
  const isCnc = name.toLocaleLowerCase("sv-SE").startsWith("cnc");
  return isCnc
    ? `${name} – CNC-maskiner till snickeriet | AS Maskiner`
    : `${name} till snickeriet | AS Maskiner`;
}

export function categoryMetaDescription(category, machineCount) {
  if (category.description) return metaDescription(category.description);

  return metaDescription(
    `${category.name} från AS Maskiner — leverantör av CNC-maskiner och snickerimaskiner i Sverige och Norden. Köp eller begär offert.` +
      (machineCount > 0 ? ` ${machineCount} maskiner i katalogen just nu.` : "")
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const { machines, categories, machineCategories } = useContext(MachineContext);
  const { locale, localePath } = useLocalePath();

  // The API list first: it carries the translated name and the description, and it knows about
  // categories that currently have no machines. The derived list is the fallback for a category
  // that only exists on the machines themselves.
  const known = useMemo(
    () => [...(categories || []), ...machineCategories],
    [categories, machineCategories]
  );

  const category = useMemo(
    () => known.find((item) => categoryUrlSlug(item) === slug) || null,
    [known, slug]
  );

  // The URL still spells the category the way the database does — a link from before the URL
  // slugs existed, or one to a slug an admin has since renamed. The server 301s these, but a
  // client-side navigation never asks the server, so it has to redirect here too.
  const legacy = useMemo(
    () => (category ? null : known.find((item) => item.slug === slug) || null),
    [category, known, slug]
  );

  const categoryMachines = useMemo(
    () => (category ? machines.filter((machine) => machine.categorySlug === category.slug) : []),
    [machines, category]
  );

  const dataLoaded = machines.length > 0 || (categories?.length ?? 0) > 0;

  // The same rule entityExists() applies in server/page.js: an empty category an admin has
  // written copy for is a real page, an empty one with nothing to say is a dead end. The two
  // must agree, or the server answers 404 for a page the client happily renders.
  const exists =
    Boolean(category) && (categoryMachines.length > 0 || Boolean(category.description));

  if (legacy) {
    return <Navigate replace to={localePath(`/catalogue/${categoryUrlSlug(legacy)}`)} />;
  }

  if (!exists) {
    // Before the first fetch resolves there is simply nothing to say yet — only call it missing
    // once some data has arrived, otherwise every client-side navigation flashes a 404.
    return dataLoaded ? <CategoryNotFound /> : null;
  }

  const path = `/catalogue/${categoryUrlSlug(category)}`;
  const absolute = (target) => absoluteUrl(locale, target);
  const url = absolute(path);
  const title = categoryTitle(category.name);
  const description = categoryMetaDescription(category, categoryMachines.length);
  const itemListElement = itemListJsonLd(absolute, categoryMachines);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {category.imageUrl && <meta property="og:image" content={category.imageUrl} />}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            url,
            name: category.name,
            description: category.description || description,
            ...(itemListElement.length > 0
              ? {
                  mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: categoryMachines.length,
                    itemListElement,
                  },
                }
              : {}),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(absolute, { name: category.name, path }))}
        </script>
      </Helmet>

      {/* The same header every other page uses: one admin-managed background video over a
          fallback image, with the category's own description read out of the database as the
          subtitle. */}
      <PageHero
        title={category.name}
        subtitle={category.description}
        backgroundKey="catalogue.category_hero_background"
        alignment="bottom"
        // These two keys ship empty and stay that way until an admin uploads something, so the
        // fallback is the site's blue rather than PageHero's light default — white title text
        // on near-white is unreadable.
        fallbackClassName="bg-primary"
      />

      <MachineGrid
        machines={categoryMachines}
        emptyFor={category.name.toLocaleLowerCase("sv-SE")}
      />

      <CategoryTiles excludeSlug={category.slug} />

      <ContactSection />
    </>
  );
}

function CategoryNotFound() {
  const { localePath } = useLocalePath();

  return (
    <div className="flex flex-col items-center justify-center h-[75vh] p-4 max-w-2xl mx-auto">
      <Helmet>
        <title>Kategorin finns inte | AS Maskiner</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="shadow-lg p-8 w-full text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary">
          <ContentText
            contentKey="landing.category_not_found"
            fallback="Den här kategorin finns inte"
          />
        </h1>
        <p className="mb-8 text-text-body">
          <ContentText
            contentKey="landing.category_not_found_body"
            fallback="Kategorin kan ha bytt namn eller tagits bort. Titta i katalogen så hittar du alla våra maskiner."
          />
        </p>
        <Link
          to={localePath("/catalogue")}
          className="inline-block bg-primary text-inverse py-2 px-6 rounded-md shadow-md hover:bg-primary800 transition-colors duration-300"
        >
          <ContentText contentKey="landing.not_found_cta" fallback="Till maskinkatalogen" />
        </Link>
      </div>
    </div>
  );
}
